import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

/* Send an approved WhatsApp template to a phone number, from job/JR pages. */
export const WhatsAppSendButton = ({ phone, contactName, contextType, contextId, contextRef, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useState([]);
  const [to, setTo] = useState(phone || "");
  const [sending, setSending] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setTo(phone || "");
    setSelected(null);
    setParams([]);
    setLoadErr("");
    setTemplates(null);
    API.get("/whatsapp/status").then(({ data }) => {
      setStatus(data);
      if (data.connected) {
        API.get("/whatsapp/templates")
          .then((r) => setTemplates(r.data))
          .catch((e) => setLoadErr(e?.response?.data?.detail || "Could not load templates"));
      }
    }).catch(() => setStatus({ connected: false }));
    // eslint-disable-next-line
  }, [open]);

  const send = async () => {
    setSending(true);
    try {
      await API.post("/whatsapp/send-template-to", {
        phone: to,
        contact_name: contactName || "",
        name: selected.name,
        language: selected.language,
        body_params: params,
        context_type: contextType,
        context_id: contextId,
        context_ref: contextRef || "",
      });
      toast.success(`WhatsApp template sent to ${to}`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  const approved = (templates || []).filter((t) => t.status === "APPROVED");

  return (
    <>
      <Button size="sm" variant="outline" className={`h-7 text-xs text-emerald-700 dark:text-emerald-400 border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 ${className}`}
              onClick={() => setOpen(true)} data-testid="wa-send-update-btn">
        <WhatsappLogo className="h-3.5 w-3.5 mr-1" weight="fill" /> WhatsApp Update
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="wa-send-update-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WhatsappLogo className="h-5 w-5 text-emerald-600" weight="fill" /> Send WhatsApp Update
            </DialogTitle>
            <DialogDescription>
              Sends a Meta-approved template{contextRef ? ` about ${contextRef}` : ""} — templates work even outside the 24-hour reply window.
            </DialogDescription>
          </DialogHeader>
          {status && !status.connected ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
              WhatsApp isn't connected yet — set it up in the <Link to="/whatsapp" className="underline font-medium">WhatsApp Inbox</Link> first.
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Recipient phone</Label>
                <Input className="mt-1" value={to} onChange={(e) => setTo(e.target.value)} data-testid="wa-send-update-phone" />
              </div>
              {loadErr && <p className="text-sm text-red-500">{loadErr}</p>}
              {!selected ? (
                <div className="max-h-56 overflow-y-auto divide-y divide-border border border-border rounded-md">
                  {templates === null && !loadErr && <p className="p-3 text-xs text-muted-foreground">Loading templates…</p>}
                  {approved.map((t) => (
                    <button key={`${t.name}-${t.language}`} onClick={() => { setSelected(t); setParams(Array(t.param_count).fill("")); }}
                            className="w-full text-left px-3 py-2 hover:bg-muted" data-testid={`wa-update-template-${t.name}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{t.name}</span>
                        <Badge variant="outline" className="text-[10px]">{t.language}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{t.body}</p>
                    </button>
                  ))}
                  {templates !== null && approved.length === 0 && (
                    <p className="p-4 text-xs text-muted-foreground">No approved templates yet — create one in Meta Business Manager → WhatsApp Manager → Message Templates.</p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-muted/40 p-3">
                    <p className="text-sm font-medium">{selected.name}</p>
                    <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{selected.body}</p>
                  </div>
                  {params.map((p, i) => (
                    <div key={i}>
                      <Label className="text-xs">Value for {"{{" + (i + 1) + "}}"}</Label>
                      <Input className="mt-1" value={p} data-testid={`wa-update-param-${i}`}
                             onChange={(e) => setParams(params.map((x, j) => (j === i ? e.target.value : x)))} />
                    </div>
                  ))}
                  <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => setSelected(null)}>Back</Button>
                    <Button onClick={send} disabled={sending || !to.trim() || params.some((p) => !p.trim())}
                            className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-send-update-confirm">
                      {sending ? "Sending…" : "Send"}
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
